<template>
    <i18n
        path="allocation-variant-styled"
        class="flex flex-row items-center font-light"
        :class="[
            {
                'text-gray-400': !Boolean(props.lightColorOverride),
                'text-2xl': props.textSize === '2XL',
                '[font-size:inherit] leading-[inherit]': props.textSize === 'Inherit',
            },
            props.lightColorOverride,
        ]"
        tag="div"
    >
        <template #allocationVariantAbbreviation>
            <Tooltip :is-teleported="true" :is-disabled="props.isTooltipDisabled">
                <template #trigger="tooltipProps">
                    <span
                        class="font-light rounded-sm tabular-nums"
                        :class="[
                            {
                                'underline decoration-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700':
                                    !props.isTooltipDisabled,
                                'text-gray-400': !Boolean(props.lightColorOverride),
                            },
                            props.lightColorOverride,
                        ]"
                        v-bind="tooltipProps"
                        :tabindex="props.isTooltipDisabled ? undefined : 0"
                    >
                        {{ $t('allocation-variant-abbreviated') }}
                    </span>
                </template>

                <template #text>{{ $t('allocation-variant-full-text') }}</template>
            </Tooltip>
        </template>

        <template #allocationVariantIndex>
            <span class="tabular-nums">
                <span
                    class="font-light"
                    :class="[{ 'text-gray-400': !Boolean(props.lightColorOverride) }, props.lightColorOverride]"
                >
                    {{ '0'.repeat(maxDigits - indexDigitsCount) }}
                </span>
                <span
                    class="font-medium"
                    :class="[{ 'text-gray-700': !Boolean(props.darkColorOverride) }, props.darkColorOverride]"
                >
                    {{ String(props.index) }}</span
                >
            </span>
        </template>
    </i18n>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type AllocationVariantIndexProps = {
    index: number;
    textSize?: 'Inherit' | '2XL';
    /**
     * Drops the abbreviation's tooltip, its dashed underline and its tab stop. Needed when this renders
     * inside another tooltip: that content is `pointer-events-none`, so a nested tooltip can never open,
     * and it is duplicated into an always-present `sr-only` label where a tab stop would be unreachable.
     */
    isTooltipDisabled?: boolean;
    lightColorOverride?: string;
    darkColorOverride?: string;
};

const maxDigits = 3;

const props = withDefaults(defineProps<AllocationVariantIndexProps>(), {
    textSize: '2XL',
});

const indexDigitsCount = computed(() => String(props.index).length);
</script>
